import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CharacteristicsModal from "../../../Component/CharacteristicsModal ";
import {ORDER_API} from  "../../../apiConfig";
import { authHeaders } from "../../../utils/authHeaders";

export default function Bd({ productDetails, selectedRfq, canReview, mode = "review", checklistItems = [], rcReviewStatus = {}, checklistMandatory = {} }) {
    // FIX 1: Store only unique IDs
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [bulkDecision, setBulkDecision] = useState("");
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkComment, setBulkComment] = useState("");
    const [expandedRows, setExpandedRows] = useState([]);
    const [selectedCharacteristics, setSelectedCharacteristics] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [partProductDetails, setPartProductDetails] = useState("");
    const [showProductModal, setShowProductModal] = useState(false);
    const [header, setHeader] = useState("");

    // NOTE: Planning Block now lives on the Product tab (editable only via
    // the main Edit/Save flow) — it's no longer shown or saved here.

    // A row needs Discount Review when its Discount (%) exceeds its
    // effective Markup (%) — the row's own Markup if it has one, else the
    // header Markup (%). Same comparison SalesOrder.js's discountReviewRows
    // uses to decide whether the Discount Review tab appears at all.
    const isDiscountFlagged = (part) => {
        const rowMarkup = (part.markup !== undefined && part.markup !== null && part.markup !== "")
            ? (parseFloat(part.markup) || 0)
            : (parseFloat(selectedRfq?.markup || 0) || 0);
        const disc = parseFloat(part.discount || 0) || 0;
        return disc > rowMarkup;
    };

    // On the main Review tab, a discount-flagged row is locked out of
    // Approve/Reject until it's been cleared in the Discount Review tab
    // (so_discount_review_action = 'Approved'). Discount Review mode itself
    // is never gated by this — that's the tab that clears it.
    const isPendingDiscountReview = (part) =>
        mode !== "discount" && isDiscountFlagged(part) && part.so_discount_review_action !== "Approved";

    // A row needs its checklist done before it can be approved here — but
    // only if that product actually has checklist parameters assigned at
    // all (checklistItems comes back empty for a product with none, so
    // those rows are never gated). Mirrors the same rule the backend
    // enforces server-side on approve, just surfaced earlier in the UI.
    // Not applicable to the Discount Review lane.
    // The "Mandatory" checkbox on the Checklist tab (checklistMandatory,
    // keyed by product_code, default true) lets someone explicitly exempt
    // one product's checklist from this gate for this SO — same override
    // the backend now checks on approve.
    const isChecklistPending = (part) => {
        if (mode === "discount") return false;
        const code = part.product_code || part.product_number;
        if (!code) return false;
        if (checklistMandatory[code] === false) return false;
        const items = checklistItems.filter((item) => item.product_code === code);
        if (items.length === 0) return false;
        const hasMissingValue = items.some((item) => {
            const v = item.value;
            return v === null || v === undefined || String(v).trim() === "";
        });
        const notDone = (rcReviewStatus[code] || "Pending") !== "Done";
        return hasMissingValue || notDone;
    };

    const isActionable = (part) => {
        // Discount Review tracks its own status column (so_discount_review_action)
        // separately from the normal Review decision (so_action), so a line
        // already cleared in one lane doesn't get hidden in the other.
        const status = mode === "discount" ? part.so_discount_review_action : part.so_action;
        const s = status ? status.toLowerCase() : "";
        // 🔥 ADDED: cancellation approved and cancelled to non-actionable states
        if (s === "approved" || s === "rejected" || s === "cancellation approved" || s === "cancelled") return false;
        // Rows still waiting on Discount Review are locked out of the
        // normal Review tab's Approve/Reject until they're cleared there.
        if (isPendingDiscountReview(part)) return false;
        // Rows with an incomplete checklist are locked out the same way.
        if (isChecklistPending(part)) return false;
        return true;
    };

    const handleSelectProduct = (e, part) => {
        if (!isActionable(part)) return;

        const rowId = part.id;

        if (e.target.checked) {
            setSelectedProducts((prev) => [...prev, rowId]);
        } else {
            setSelectedProducts((prev) => prev.filter((item) => item !== rowId));
        }
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            const selectableIds = productDetails
                .filter((product) => isActionable(product))
                .map((product) => product.id);

            setSelectedProducts(selectableIds);
        } else {
            setSelectedProducts([]);
        }
    };

    const submitDecision = async (decisionType) => {
        if (selectedProducts.length === 0) {
            toast.warning("Please select at least one product!");
            return;
        }
        const identifier = selectedRfq.so_id || selectedRfq.rfq_id;

        // Defensive guard — selectedProducts should never contain a row
        // still pending Discount Review (checkboxes are disabled for
        // those), but block it here too rather than letting the backend
        // reject it as a raw "Failed to submit decision" toast.
        if (mode !== "discount") {
            const blocked = productDetails.filter(p => selectedProducts.includes(p.id) && isPendingDiscountReview(p));
            if (blocked.length > 0) {
                toast.error("Some selected items are pending Discount Review. Please clear them in the Discount Review tab first.");
                return;
            }
            const checklistBlocked = productDetails.filter(p => selectedProducts.includes(p.id) && isChecklistPending(p));
            if (checklistBlocked.length > 0) {
                toast.error("Some selected items have an incomplete checklist. Please fill it in and mark it Done in the Checklist tab first.");
                return;
            }
        }

        // 🔥 SMART ROUTING: Decide action PER ITEM based on its current state
        const partsToSubmit = productDetails
            .filter((part) => selectedProducts.includes(part.id))
            .map((part) => {
                if (mode === "discount") {
                    // Discount Review is a separate lane — Approve/Reject here
                    // only ever writes so_discount_review_*, never so_action.
                    return {
                        id: part.id,
                        so_discount_review_action: decisionType.trim() === "Approve" ? "Approved" : "Rejected",
                        so_discount_review_comment: bulkComment,
                        so_discount_review_by: getCurrentUser(),
                    };
                }

                let finalAction = decisionType.trim(); // Will be "Approve" or "Reject"
                const currentAction = part.so_action ? part.so_action.toLowerCase() : "";

                // Translate to Backend terminology strictly based on item's individual state
                if (finalAction === "Approve") {
                    finalAction = currentAction === "cancel initiated" ? "cancellation approved" : "approved";
                } else if (finalAction === "Reject") {
                    finalAction = currentAction === "cancel initiated" ? "cancel_rejected" : "rejected";
                }

                return {
                    id: part.id,
                    so_action: finalAction,
                    so_comment: bulkComment,
                    so_action_by: getCurrentUser(),
                };
            });

        const endpoint = mode === "discount" ? "update-discount-review-action" : "update-product-action";

        try {
            const response = await fetch(`${ORDER_API}/sales-order/${identifier}/${endpoint}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({ parts: partsToSubmit }),
            });

            const result = await response.json().catch(() => ({}));

            if (response.ok) {
                // toast.success(`${decisionType} submitted successfully`);
                toast.success("Submitted Successfully");
                setTimeout(() => window.location.reload(), 1000);
            } else {
                toast.error(result.message || "Failed to submit decision");
            }
        } catch (error) {
            console.error("Submit Error:", error);
            toast.error("Error submitting decision");
        }
    };

    const toggleRowExpansion = (index, part) => {
        setExpandedRows((prevState) => {
            const newState = [...prevState];
            newState[index] = !newState[index];
            if (newState[index] && part.characteristics) {
                setSelectedCharacteristics(part.characteristics);
                setModalVisible(true);
            }
            return newState;
        });
    };

    const handleCharCloseModal = () => setModalVisible(false);

    const handleViewProductDetails = async (id) => {
        try {
            const response = await fetch(`${ORDER_API}/sales-order/product-details/${id}`, {
                headers: { ...authHeaders() }
            });

            if (response.ok) {
                const data = await response.json();
                setPartProductDetails(data);
                setShowProductModal(true);
            } else {
                toast.error("Failed to fetch details");
            }
        } catch (error) {
            console.error("Error fetching product details:", error);
        }
    };

    const selectableItemsCount = productDetails.filter(p => isActionable(p)).length;

    // The status column shows so_action normally, or so_discount_review_action
    // in Discount Review mode — same field the Approve/Reject buttons act on.
    const statusField = (part) => mode === "discount" ? part.so_discount_review_action : part.so_action;
    const isAllSelectableSelected = selectableItemsCount > 0 && selectableItemsCount === selectedProducts.length;

    // Count of rows on the main Review tab still waiting on Discount
    // Review — drives the banner above the action buttons.
    const pendingDiscountReviewCount = mode !== "discount"
        ? productDetails.filter(isPendingDiscountReview).length
        : 0;

    // Count of rows still waiting on their checklist — drives its own
    // banner, separate from the Discount Review one above.
    const pendingChecklistCount = mode !== "discount"
        ? productDetails.filter((p) => isChecklistPending(p) && !isPendingDiscountReview(p)).length
        : 0;

    const handleChange = () => {
        toast.info("You cannot change in Review");

    };

    const getCurrentUser = () => {
        // Try full user object first (your login stores: { email, role, token, ... })
        const userObj = localStorage.getItem("user");
        if (userObj) {
            try {
                const parsedUser = JSON.parse(userObj);
                // Prefer the actual user id (e.g. "maithili") so Approved By / review
                // stamps store the user id, not the name or email.
                if (parsedUser.userId) return parsedUser.userId;
                // Then name fields, fallback to email
                const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
                if (fullName) return fullName;
                if (parsedUser.email) return parsedUser.email;
            } catch (e) { console.error("Error parsing user data:", e); }
        }
        // Also check if token is stored separately and email is stored flat
        const storedUserId = localStorage.getItem("userId");
        if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;
        const email = localStorage.getItem("email");
        if (email && email !== "undefined" && email !== "null") return email;
        const storedName = localStorage.getItem("userName");
        if (storedName && storedName !== "undefined" && storedName !== "null") return storedName;
        return "User";
    };

    const formatSoActionLabel = (action) => {
    if (!action) return "";
    const map = {
        "approved": "Approved",
        "rejected": "Rejected",
        "cancel initiated": "Cancellation Initiated",
        "cancel_requested": "Cancellation Requested",
        "cancel_rejected": "Cancellation Rejected",
        "cancellation approved": "Cancellation Approved",
    };
    return map[action.toLowerCase()] || action;
};

    const formatCurrency = (value) => {
        const val = Number(value);
        if (isNaN(val)) return "0";
        return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Status -> color, so Approved reads green and Rejected reads red at a
    // glance; everything else (Pending, Cancellation states, etc.) stays the
    // default text color.
    const getStatusColor = (action) => {
        const s = action ? action.toLowerCase() : "";
        if (s === "approved" || s === "cancellation approved") return "#16a34a"; // green
        if (s === "rejected" || s === "cancel_rejected") return "#dc2626"; // red
        return "#334155"; // default
    };


    return (
        <div>
            <hr />
            {pendingDiscountReviewCount > 0 && (
                <div style={{
                    background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412',
                    borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13
                }}>
                    ⚠ {pendingDiscountReviewCount} item{pendingDiscountReviewCount > 1 ? 's' : ''} exceed{pendingDiscountReviewCount > 1 ? '' : 's'} their Markup (%) with a higher Discount (%) and need to be cleared in the <b>Discount Review</b> tab before they can be approved here.
                </div>
            )}
            {pendingChecklistCount > 0 && (
                <div style={{
                    background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412',
                    borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13
                }}>
                    ⚠ {pendingChecklistCount} item{pendingChecklistCount > 1 ? 's have' : ' has'} an incomplete checklist and need{pendingChecklistCount > 1 ? '' : 's'} to be filled in and marked Done in the <b>Checklist</b> tab before they can be approved here.
                </div>
            )}
            <div className="d-flex gap-2 mb-3" style={{ justifyContent: 'flex-end' }}>
                <Button
                    style={styles.primaryBtn}
                    variant="dark"
                    disabled={selectedProducts.length === 0 || !canReview}
                    title={!canReview ? "Your role does not have Review access" : undefined}
                    onClick={() => {
                        // 🔥 SMART LOGIC: Check for cancel initiated
                        const isCancelFlow = mode !== "discount" && productDetails.some(p => selectedProducts.includes(p.id) && p.so_action?.toLowerCase() === 'cancel initiated');

                        setBulkDecision("Approve");
                        setHeader(isCancelFlow ? "Approval/Cancellation" : "Approve");
                        setBulkComment("");
                        setShowBulkModal(true);
                    }}>
                    Approve
                </Button>
                <Button
                    style={styles.primaryBtn}
                    variant="dark"
                    disabled={selectedProducts.length === 0 || !canReview}
                    title={!canReview ? "Your role does not have Review access" : undefined}
                    onClick={() => {
                        // 🔥 SMART LOGIC: Check for cancel initiated
                        const isCancelFlow = mode !== "discount" && productDetails.some(p => selectedProducts.includes(p.id) && p.so_action?.toLowerCase() === 'cancel initiated');

                        setBulkDecision("Reject");
                        setHeader(isCancelFlow ? "Reject Cancellation" : "Reject");
                        setBulkComment("");
                        setShowBulkModal(true);
                    }}>
                    Reject
                </Button>
            </div>

            <div style={styles.tableContainer}>
                <table className="table mb-0" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={styles.erpTh}>
                                <input
                                    type="checkbox"
                                    checked={isAllSelectableSelected}
                                    disabled={selectableItemsCount === 0}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                />
                            </th>
                            <th style={styles.erpTh}>Item</th>
                            <th style={styles.erpTh}>Product Code</th>
                            <th style={styles.erpTh}>Description</th>
                            <th style={styles.erpTh}>Fast entry</th>
                            <th style={styles.erpTh}>UOM</th>
                            <th style={styles.erpTh}>Currency</th>
                            <th style={styles.erpTh}>Unit Price</th>
                            <th style={styles.erpTh}>Quantity</th>
                            <th style={styles.erpTh}>Total Price</th>
                            <th style={styles.erpTh}>Requirement Date</th>
                            <th style={styles.erpTh}>Discount</th>
                            <th style={styles.erpTh}>Status</th>
                            <th style={styles.erpTh}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {productDetails.map((part, idx) => {
                            const isFinalized = !isActionable(part);
                            const pendingDiscount = isPendingDiscountReview(part);

                            return (
                                <tr key={part.id} style={isFinalized ? { backgroundColor: "#f8f9fa", color: "#6c757d" } : {}}>
                                    <td style={styles.erpTd}>
                                        <input
                                            type="checkbox"
                                            disabled={isFinalized}
                                            title={pendingDiscount ? "Pending Discount Review" : undefined}
                                            checked={selectedProducts.includes(part.id)}
                                            onChange={(e) => handleSelectProduct(e, part)}
                                        />
                                    </td>
                                    <td style={styles.erpTd}>{idx + 1}</td>
                                    <td style={styles.erpTd}>{part.product_code}</td>
                                    <td style={styles.erpTd}>{part.description}</td>
                                    <td style={styles.erpTd} className='text-start'>
                                        {part.product_class}
                                        <span
                                            style={{ cursor: 'pointer', fontSize: '16px', marginLeft: '10px' }}
                                            onClick={() => toggleRowExpansion(idx, part)}
                                        >
                                            {expandedRows[idx] ? '▼' : '▶'}
                                        </span>
                                    </td>
                                    <td style={styles.erpTd}>{part.uom}</td>
                                    <td style={styles.erpTd}>{part.currency || part.Currency || ""}</td>
                                    <td style={styles.erpTd}>{part.unit_price}</td>
                                    <td style={styles.erpTd}>{part.quantity}</td>
                                    <td style={styles.erpTd}>
                                        {part.total_price}
                                    </td>
                                    <td style={styles.erpTd}>
                                        {part.requirement_date ? (() => {
                                            const d = new Date(part.requirement_date);
                                            const day = String(d.getDate()).padStart(2, '0');
                                            const month = String(d.getMonth() + 1).padStart(2, '0');
                                            const year = d.getFullYear();
                                            return `${day}/${month}/${year}`;
                                        })() : "N/A"}
                                    </td>

                                    <td style={styles.erpTd}>{part.discount}</td>
                                    <td style={{ ...styles.erpTd, color: pendingDiscount ? '#9a3412' : getStatusColor(statusField(part)), fontWeight: 600 }}>
                                        {pendingDiscount ? "Pending Discount Review" : (formatSoActionLabel(statusField(part)) || "Pending")}
                                    </td>
                                    <td style={styles.erpTd} className='text-start'>
                                        <button className="btn btn-sm btn-dark" onClick={() => handleViewProductDetails(part.id)}>View</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <CharacteristicsModal
                modalVisible={modalVisible}
                selectedCharacteristics={selectedCharacteristics}
                handleClose={handleCharCloseModal}
                handleChange={handleChange}
                isEditing={false}
            />

            {showProductModal && productDetails && (
                <div style={styles.modalBackdrop}>
                    <div
                        style={{
                            ...styles.modal,
                            maxWidth: "950px",
                            width: "95%",
                            maxHeight: "85vh",
                            padding: "14px",
                            fontSize: "13px"
                        }}
                    >
                        <div style={{ padding: "12px" }}>
                            <table className="table table-bordered table-sm mb-3">
                                <thead style={{ background: "#f8fafc", fontWeight: 600 }}>
                                    <tr>
                                        <th>Product Code</th>
                                        <th>HSN Code</th>
                                        <th>Quantity</th>
                                        <th>Standard Price</th>
                                        <th>Discount (%)</th>
                                        <th>Total Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{partProductDetails.product_number}</td>
                                        <td>{partProductDetails.hsn_code}</td>
                                        <td>{partProductDetails.quantity}</td>
                                        <td>₹ {formatCurrency(partProductDetails.unit_price)}</td>
                                        <td>{partProductDetails.discount}</td>
                                        <td style={{ fontWeight: 600 }}>
                                            ₹ {formatCurrency(partProductDetails.totalPrice)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "8px 10px",
                                    borderTop: "1px solid #999",
                                    borderBottom: "1px solid #999",
                                    marginBottom: "10px",
                                    fontWeight: 600
                                }}
                            >
                                <span>Net Price :</span>
                                <span>₹ {formatCurrency(partProductDetails.netPrice)}</span>
                            </div>

                            <table
                                className="table table-sm"
                                style={{ width: "50%", marginLeft: "auto" }}
                            >
                                <tbody>
                                    <tr>
                                        <td>CGST @ {partProductDetails.cgst || 0}%</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {formatCurrency(partProductDetails.cgstAmount || 0)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>SGST @ {partProductDetails.sgst || 0}%</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {formatCurrency(partProductDetails.sgstAmount || 0)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>IGST @ {partProductDetails.igst || 0}%</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {formatCurrency(partProductDetails.igstAmount || 0)}
                                        </td>
                                    </tr>
                                    <tr style={{ borderTop: "1px solid #000", fontWeight: 600 }}>
                                        <td>Total GST</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {formatCurrency(partProductDetails.totalGST)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "12px",
                                    marginTop: "8px",
                                    background: "#f1f5f9",
                                    fontWeight: 700,
                                    fontSize: "15px",
                                    borderRadius: "4px"
                                }}
                            >
                                <span>Grand Total :</span>
                                <span>₹ {formatCurrency(partProductDetails.finalPriceAfterGST)}</span>
                            </div>
                        </div>

                        <div style={{ ...styles.modalFooter, padding: "10px 14px" }}>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setShowProductModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showBulkModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "600px", width: "100%" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600 }}>{`${header} Sales Order `}</div>
                            <button style={styles.iconBtn} onClick={() => setShowBulkModal(false)}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            <textarea
                                value={bulkComment}
                                onChange={(e) => setBulkComment(e.target.value)}
                                placeholder="Enter your comment here"
                                rows="4"
                                style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", resize: "none" }}
                            />
                        </div>
                        <div style={styles.modalFooter}>
                            <button className="btn btn-outline-secondary" onClick={() => setShowBulkModal(false)}>Cancel</button>
                            <button className="btn btn-dark" onClick={() => submitDecision(bulkDecision)}>Submit</button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />

        </div>
    );
}

const styles = {
    compactLabel: { fontSize: "14px", fontWeight: 600, marginBottom: "4px" },
    compactInput: { fontSize: "14px", height: "32px", padding: "4px 8px" },
    compactTabBtn: { fontSize: "14px", padding: "4px 10px" },
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 13, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 18 },
    filterLabel: { fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#334155' },
    filterSelect: { height: 36, fontSize: 13 },
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 26, fontWeight: 700, color: '#1e293b', marginBottom: 2 },
    pageSubtitle: { fontSize: 13, color: '#64748b' },
    primaryBtn: { paddingLeft: 14, paddingRight: 14 },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px' },
    modal: { background: '#fff', borderRadius: '8px', width: '90%', maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    modalHeader: { padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    iconBtn: { background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer' },
    modalBody: { padding: '20px', overflowY: 'auto', flex: 1 },
    modalFooter: { padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
    tabs: { display: 'flex', gap: 8, margin: '14px 0' },
};